// ── Store selection ──────────────────────────────────────────────────────────
// Active store: Tindie. To revert to Lectronz, swap the commented blocks below,
// then do the same in scripts/prerender.mjs, public/index.html and
// public/llms.txt (search for "tindie"), and rebuild.
export const storeName = 'Tindie';
const buyLinks = {
  'logic-gates': 'https://www.tindie.com/products/37331/',
  'logic-learner': 'https://www.tindie.com/products/40140/',
};
// export const storeName = 'Lectronz';
// const buyLinks = {
//   'logic-gates': 'https://lectronz.com/products/logic-gates',
//   'logic-learner': 'https://lectronz.com/products/logic-trainer-digital-electronics-learning-kit',
// };

// Technical specifications and data for Logic Gates products
export const products = [
  {
    id: 'logic-gates',
    name: 'Logic Gates',
    tagline: 'boolean algebra = interactive_learning();',
    description: 'A hands-on educational PCB featuring different logic gates with real-time LED visualization and truth table.',
    price: '$25',
    binaryPrice: '0b11001.00000000', // 25.00 in binary representation
    hexPrice: '0x19.00',
    image: '/images/logic%20gates/1.webp',
    gallery: [
      {
        url: '/images/logic%20gates/1.webp',
      },
      {
        url: '/images/logic%20gates/2.webp',
      },
      {
        url: '/images/logic%20gates/3.webp',
      },
      {
        url: '/images/logic%20gates/4.webp',
      },
      {
        url: '/images/logic%20gates/5.webp',
      }
    ],
    features: [
      'Indicative LEDs - When a switch is turned on (high) a LED lights up to indicate the active state',
      'Truth table - Helps to explain when the output of the circuit will be high or low, based on the inputs',
      'Satisfying click - The switches used provide a satisfying click when operated',
      'Turn on animation - When the board is powered on, a cool animation sequence plays',
      'Complete Boolean operator set: ∧ (AND), ∨ (OR), ¬ (NOT), ↑ (NAND), ↓ (NOR), ⊕ (XOR), ⊙ (XNOR)'
    ],
    technicalSpecs: {
      'Substrate': 'FR-4 Epoxy Fiberglass (1.6mm)',
      'Logic Family': '74HC Series CMOS',
      'Supply Voltage': '4.5V - 5.5V DC (VCC)',
      'Input Logic Levels': 'VIL: 0-1.35V, VIH: 3.15-5V',
      'Output Drive': 'IOH: -4mA, IOL: 4mA',
      'Propagation Delay': 'tpd: 8ns (typical @ 5V)',
      'Operating Temperature': '-40°C to +125°C',
      'Dimensions': '150mm × 100mm × 1.6mm',
      'Connector Type': '2.54mm pitch headers',
      'LED Specifications': 'Forward Current: 20mA, Vf: 2.0V'
    },
    codeExamples: [
      {
        language: 'Python',
        code: `# Truth table verification
def verify_logic_gate(gate_type, inputs):
    if gate_type == "AND":
        return all(inputs)
    elif gate_type == "OR": 
        return any(inputs)
    elif gate_type == "NOT":
        return not inputs[0]
    # ... implement other gates`
      },
      {
        language: 'Verilog',
        code: `// Hardware description
module logic_gates_tb;
  reg a, b;
  wire and_out, or_out, not_out;
  
  assign and_out = a & b;
  assign or_out = a | b;
  assign not_out = ~a;
endmodule`
      }
    ],
    specialDescription: `The Logic Gates board is the perfect first step to start learning about logic gates.

Flip a switch, see the output light up, and watch the printed truth table on the silkscreen confirm exactly what's happening. No software, no soldering, no breadboarding — just 2×AAA batteries and you're learning.

**Each gate has its own dedicated switches, output LED, and printed truth table** — so you can compare what the table says against what the LEDs do, gate by gate, in your own time.

Every gate on the board is explained in the companion wiki at [logicgat.es/logic-wiki](/logic-wiki) — with diagrams, truth tables, and real-world analogies (XOR as a hallway light, AND as a safety interlock, and so on).

**Certified open source hardware.** Logic Gates is [OSHWA-certified (EE000005)](https://certification.oshwa.org/ee000005.html) — the complete design, schematics and all, is freely available in [the project repository on GitLab](https://gitlab.com/oliver.vaht/logic-gates). Nothing hidden, nothing locked down: study it, modify it, build your own. And every board you buy helps fund more open source hardware like this.

Ready for more? Once single gates feel easy, the next step is combining them into real circuits with clocks, counters, memory, and a 7-segment display. That's exactly what the [Logic Trainer](/product/logic-learner) is built for — same hands-on philosophy, expanded scope. Logic Gates is the foundation; the Logic Trainer is where you build on it.`,
    youtubeVideo: 'i6UmRWPSrDw',
    reviewVideos: [
      {
        title: 'EEVblog 1690 - Mailbag: Zifnu LED, Quantum Diamonds, Logic Gates, M.2 NAS',
        youtubeId: 'CktljgyKILE',
        reviewer: 'EEVblog',
        rating: null,
        views: '23K+',
        startTime: '17:15',
        note: 'Watch from 17:15'
      },
      {
        title: 'Beginners Guide To Logic Gates In Electronics - Explained & Demonstrated',
        youtubeId: 'qsKnl521y7k',
        reviewer: 'JazzTech Electronics',
        rating: null,
        views: '2.7K+'
      }
    ],
    inStock: true,
    modelType: 'pcb',
    buyLink: buyLinks['logic-gates']
  },
  {
    id: 'logic-learner',
    name: 'Logic Trainer',
    tagline: 'while(learning) { understand++; }',
    description: 'An interactive logic trainer with a built-in clock, memory, and display — unlocking endless ways to explore and truly understand digital logic.',
    price: '$98.00',
    binaryPrice: '0b1100010.00000000', // 98.00 in binary
    hexPrice: '0x62.00',
    image: '/images/logic%20trainer/1.webp',
    gallery: [
      {
        url: '/images/logic%20trainer/1.webp',
      },
      {
        url: '/images/logic%20trainer/2.webp',
      },
      {
        url: '/images/logic%20trainer/3.webp',
      },
      {
        url: '/images/logic%20trainer/4.webp',
      },
      {
        url: '/images/logic%20trainer/5.webp',
      },
      {
        url: '/images/logic%20trainer/6.webp',
      }
    ],
    features: [
      'Clock from 0.2Hz to 5Hz or do it manually',
      'Connect different inputs and outputs together',
      'Nearly 700 components on the PCB',
      'Two (for all) buffers, NOT, AND, OR, XOR, NAND, NOR, XNOR logic gates',
      '7 segment display to show the numbers',
      '3xAAA batteries power the board',
      '4 bit Memory to store the values',
      'Multiplexer to choose the output',
      'Binary-Coded Decimal Counter',
      '8 bit Shift Register',
      'Output LEDs'
    ],
    technicalSpecs: {
      'Logic Family': '74HC Series CMOS',
      'Gates': '16 gates (2× each: BUFFER, NOT, AND, NAND, XOR, XNOR, OR, NOR)',
      'Clock': '0.2 Hz – 5 Hz adjustable, or manual step',
      'Memory': '4-bit memory, 8-bit shift register',
      'Counter': 'Binary-Coded Decimal counter',
      'Display': '7-segment display + output LEDs',
      'Routing': 'Multiplexer, jumper-wire connections',
      'Robustness': 'Tolerant to incorrect jumper connections',
      'Component Count': 'Nearly 700 components',
      'Power Supply': '3×AAA batteries'
    },
    codeExamples: [
      {
        language: 'Verilog',
        code: `// Configurable logic block
module clb_4lut #(
    parameter [15:0] LUT_INIT = 16'h0000
)(
    input [3:0] in,
    output out
);
    assign out = LUT_INIT[in];
endmodule`
      },
      {
        language: 'MicroPython',
        code: `# LED matrix control
from machine import Pin, SPI
import neopixel

led_matrix = neopixel.NeoPixel(Pin(5), 64)
def display_pattern(pattern):
    for i, color in enumerate(pattern):
        led_matrix[i] = color
    led_matrix.write()`
      }
    ],
    youtubeVideo: 'G3EfUcMvEQo',
    reviewVideos: [
      {
        title: 'Logic Gates Trainer Board - A Very Cool Learning Tool For Logic In Electronics!',
        youtubeId: 'lpRVNDihWrU',
        reviewer: 'JazzTech Electronics',
        rating: null,
        views: '2.7K+',
        note: 'Thanks Jazzy for the great review!'
      },
      {
        title: 'EEVblog 1720 - Mailbag: USSR Bonanza + Logic Training + $69 Logic Analyzer',
        youtubeId: 'G3EfUcMvEQo',
        reviewer: 'EEVblog',
        rating: null,
        views: '23k+',
        startTime: '26:50',
        note: 'I was honored to be featured on the EEVblog again, thank you Dave!'
      }
    ],
    inStock: true,
    comingSoon: false,
    isNew: false,
    releaseDate: 'Available Now',
    modelType: 'trainer',
    buyLink: buyLinks['logic-learner'],
    specialDescription: `The Logic Trainer — learn digital logic by building real circuits, not watching simulations.

A standalone, battery-powered board that lets you wire up logic gates with jumpers and see results immediately on LEDs and a 7-segment display. No soldering, no power supply, no software — plug in 3×AAA batteries and start building.

Trusted by the electronics community. Our earlier project Logic Gates was featured on EEVblog and reviewed by JazzTech Electronics. The Logic Trainer is the next iteration, redesigned around feedback from learners, universities, and the global hobbyist community.

What makes it different:

**A built-in variable clock for sequential logic experiments** (0.2 Hz to 5 Hz, or step manually) — flip-flops, counters, and shift registers become tangible, not just diagrams.

**Jumper-tolerant design** — incorrect connections won't damage anything. Experiment freely.

**Companion curriculum included** — structured exercises designed specifically for this board, freely available at [logicgat.es/logic-wiki](/logic-wiki).

A concrete example: in under 15 minutes and a handful of jumpers, a learner can wire the BCD counter to the 7-segment display, drive it with the onboard clock, and watch a working digital counter come to life — then slow the clock to step through each state and see how sequential logic actually behaves.

Who it's for:
Parents learning alongside their kids — no electronics background required; the wiki walks you through every step.
Students working through a digital logic course who want hardware to match their textbook.
Educators looking for a classroom-ready, durable, low-setup teaching tool.

Get started: Browse the full wiki, exercises, and example builds at [logicgat.es/logic-wiki](/logic-wiki).

Bulk or classroom orders: [contact us here](#contact).`
  }
];

export const companyInfo = {
  name: 'Logic Gates',
  website: 'logicgat.es', 
  tagline: 'Making Digital Logic Learning Interactive',
  description: 'Digital logic, made simple. Our Logic Gates and Logic Trainer boards turn abstract concepts into clear, hands-on experiments.',
  mission: 'Empowering engineers through interactive learning.',
  foundedYear: 2024
};

// Technical constants for CS nerds
export const techConstants = {
  booleanOperators: ['∧', '∨', '¬', '↑', '↓', '⊕', '⊙'],
  logicLevels: { HIGH: '1', LOW: '0', UNDEFINED: 'X', HIGH_IMPEDANCE: 'Z' },
  commonFrequencies: ['1Hz', '10Hz', '1kHz', '1MHz', '100MHz'],
  colorCodes: {
    pcbGreen: '#0D7850',
    pcbBlack: '#1a1a1a', 
    silkWhite: '#FFFFFF',
    ledYellow: '#FFEB3B',
    copperGold: '#FFD700'
  }
};
